import { css } from '@emotion/react'
import {
  Heading,
  Link,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'
import { Fragment, type ReactNode } from 'react'

import { getMarketplaceTermsLink } from '@/utils/products'
import InfoAlert from '../InfoAlert'

const alertCss = css`
  margin-bottom: var(${SpacingToken.Spacing7});

  [class*='__alert__content__wrapper__text'] {
    max-width: 100%;
  }
`

const titleCss = css`
  && {
    margin-bottom: var(${SpacingToken.Spacing2});
  }
`

const termsLink = getMarketplaceTermsLink()

const marketplaceTerms = (
  <Link href={termsLink} target="_blank" rel="noopener">
    Marketplace Terms
  </Link>
)

const getTermsCopy = (
  hasScopes: boolean,
  privacyPolicyUrl?: string,
  termsAndConditionsUrl?: string
): ReactNode[] => {
  const privacyPolicyLink = (
    <Link
      href={privacyPolicyUrl}
      target="_blank"
      rel="noopener"
      key="privacyPolicyLink"
    >
      App Privacy Policy
    </Link>
  )

  const termsAndConditionsLink = (
    <Link
      href={termsAndConditionsUrl}
      target="_blank"
      rel="noopener"
      key="termsAndConditionsLink"
    >
      App&nbsp;Terms and Conditions
    </Link>
  )

  const scopesText = 'Scopes listed in this section'

  const copyArray: ReactNode[] = []

  if (privacyPolicyUrl) {
    copyArray.push(privacyPolicyLink)
  }

  if (termsAndConditionsUrl) {
    copyArray.push(termsAndConditionsLink)
  }

  if (hasScopes) {
    copyArray.push(scopesText)
  }

  const copy: ReactNode[] = [
    <Fragment key="baseCopy">
      By installing this app, you agree to the {marketplaceTerms}
    </Fragment>,
  ]

  copyArray.forEach((value, index, arr) => {
    if (arr.length - 1 === index) {
      copy.push(' and ', value)
    } else {
      copy.push(', ', value)
    }
  })
  copy.push('.')

  return copy
}

interface Props {
  privacyPolicyUrl?: string
  hasScopes: boolean
  termsAndConditionsUrl?: string
}

export const TermsBanner = ({
  privacyPolicyUrl,
  hasScopes,
  termsAndConditionsUrl,
}: Props) => {
  const copy = getTermsCopy(hasScopes, privacyPolicyUrl, termsAndConditionsUrl)
  return (
    <InfoAlert data-test="legal-terms-section" css={alertCss}>
      <Heading size="xs" css={titleCss}>
        Legal Terms
      </Heading>
      <Text>{copy}</Text>
    </InfoAlert>
  )
}
